/**
 * Google Cloud design tokens.
 *
 * Colors sourced from the Google Cloud Console brand system and Material Design 3
 * tonal palettes. We expose them as a flat token object and feed them into MUI's
 * createTheme() in `theme/index.ts`.
 */

export const palette = {
  blue: {
    50: '#e8f0fe',
    100: '#d2e3fc',
    200: '#aecbfa',
    300: '#8ab4f8',
    400: '#669df6',
    500: '#4285f4',
    600: '#1a73e8',
    700: '#1967d2',
    800: '#185abc',
    900: '#174ea6',
  },
  grey: {
    50: '#f8f9fa',
    100: '#f1f3f4',
    200: '#e8eaed',
    300: '#dadce0',
    400: '#bdc1c6',
    500: '#9aa0a6',
    600: '#80868b',
    700: '#5f6368',
    800: '#3c4043',
    900: '#202124',
  },
  green: {
    50: '#e6f4ea',
    500: '#34a853',
    700: '#188038',
    900: '#0d652d',
  },
  yellow: {
    50: '#fef7e0',
    500: '#fbbc04',
    700: '#f29900',
    900: '#ea8600',
  },
  red: {
    50: '#fce8e6',
    500: '#ea4335',
    700: '#d93025',
    900: '#a50e0e',
  },
  cyan: {
    50: '#e4f7fb',
    500: '#12b5cb',
    700: '#098591',
  },
} as const;

export const semantic = {
  success: palette.green[700],
  warning: palette.yellow[700],
  error: palette.red[700],
  info: palette.blue[600],
} as const;

/** Material Design 3 elevation tokens (shadows). */
export const elevation = {
  0: 'none',
  1: '0 1px 2px 0 rgba(60,64,67,0.30), 0 1px 3px 1px rgba(60,64,67,0.15)',
  2: '0 1px 2px 0 rgba(60,64,67,0.30), 0 2px 6px 2px rgba(60,64,67,0.15)',
  3: '0 4px 8px 3px rgba(60,64,67,0.15), 0 1px 3px 0 rgba(60,64,67,0.30)',
  4: '0 6px 10px 4px rgba(60,64,67,0.15), 0 2px 3px 0 rgba(60,64,67,0.30)',
  5: '0 8px 12px 6px rgba(60,64,67,0.15), 0 4px 4px 0 rgba(60,64,67,0.30)',
} as const;

export const radii = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  pill: 999,
} as const;

/** 8px baseline grid per Material Design. */
export const spacing = 8;

export const typography = {
  fontFamily: {
    sans: '"Google Sans Text", "Roboto", "Helvetica", "Arial", sans-serif',
    display: '"Google Sans", "Google Sans Text", "Roboto", sans-serif',
    mono: '"Roboto Mono", ui-monospace, SFMono-Regular, monospace',
  },
} as const;
